import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Calendar,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Spin,
  Tag,
  TimePicker,
  message,
} from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  Plus,
} from "lucide-react";
import {
  SaleBooking,
  createBooking,
  fetchSalesBookings,
  updateBookingStatus,
  rescheduleBooking,
} from "../../store/salesSlice";
import { fetchClients } from "../../store/clientsSlice";
import { setCheckoutContext, resetFlow, fetchTaxRate } from "../../store/posSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import axiosClient from "../../api/axiosClient";
import "./AdminSalesPage.css";

type Transaction = {
  transaction_id: string;
  amount: number;
  payment_method: string;
  tendered_amount?: number;
  change_due?: number;
  created_at: string;
};

function authHeader(): Record<string, string> {
  const token = window.localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

type RealService = {
  service_id: string;
  name: string;
  price: number;
  duration_min: number;
};

function initials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function durationMinutes(start: string, end: string) {
  const mins = (new Date(end).getTime() - new Date(start).getTime()) / 60000;
  return `${mins} min`;
}

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  scheduled:    { color: "default", label: "Upcoming" },
  in_progress:  { color: "blue",    label: "In Progress" },
  completed:    { color: "green",   label: "Completed" },
  cancelled:    { color: "red",     label: "Cancelled" },
  no_show:      { color: "orange",  label: "No Show" },
};

export default function AdminSalesPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { bookings, loading, updatingId } = useAppSelector((s) => s.sales);

  const [businessId, setBusinessId] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [searchQuery, setSearchQuery] = useState("");
  const [apptModalOpen, setApptModalOpen] = useState(false);
  const [apptForm] = Form.useForm();
  const [apptSaving, setApptSaving] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const { clients } = useAppSelector((s) => s.clients);
  const [barberList, setBarberList] = useState<{ id: string; name: string }[]>([]);
  const [services, setServices] = useState<RealService[]>([]);
  const [viewingReceipt, setViewingReceipt] = useState<Transaction | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [reschedulingBooking, setReschedulingBooking] = useState<SaleBooking | null>(null);
  const [rescheduleForm] = Form.useForm();
  const [rescheduleSaving, setRescheduleSaving] = useState(false);

  // Resolve businessId from /me on mount
  useEffect(() => {
    const token = window.localStorage.getItem("accessToken");
    if (!token) return;
    axiosClient
      .get("/me", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data as { business_id?: string };
        if (data.business_id) setBusinessId(data.business_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!businessId) return;
    dispatch(fetchSalesBookings({ businessId, date: selectedDate.format("YYYY-MM-DD") }));
  }, [businessId, selectedDate, dispatch]);

  // Keep clients, barbers, and services loaded for the modal
  useEffect(() => {
    if (!businessId) return;
    dispatch(fetchClients(businessId));
    const token = window.localStorage.getItem("accessToken");
    axiosClient
      .get("/calendar/barbers", { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => {
        const data = res.data as { user_id: string; name: string }[];
        setBarberList(data.map((b) => ({ id: b.user_id, name: b.name })));
      })
      .catch(() => {});
    axiosClient
      .get("/settings/services", { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => setServices(res.data ?? []))
      .catch(() => setServices([]));
  }, [businessId, dispatch]);

  function openApptModal() {
    apptForm.resetFields();
    apptForm.setFieldsValue({ date: selectedDate, start_time: dayjs().hour(9).minute(0) });
    setApptModalOpen(true);
  }

  async function handleApptSubmit(values: any) {
    if (!businessId) return;
    const svc = services.find((s) => s.name === values.service);
    const startDt: Dayjs = values.date
      .hour(values.start_time.hour())
      .minute(values.start_time.minute())
      .second(0);
    const endDt = startDt.add(svc?.duration_min ?? 60, "minute");

    setApptSaving(true);
    const result = await dispatch(
      createBooking({
        client_id: values.client_id,
        staff_id: values.staff_id,
        service: values.service,
        service_id: svc?.service_id,
        start_time: startDt.toISOString(),
        end_time: endDt.toISOString(),
        price: values.price,
      })
    );
    setApptSaving(false);

    if (createBooking.fulfilled.match(result)) {
      messageApi.success("Appointment created");
      setApptModalOpen(false);
      dispatch(fetchSalesBookings({ businessId, date: selectedDate.format("YYYY-MM-DD") }));
    } else {
      messageApi.error("Failed to create appointment");
    }
  }

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return bookings;
    return bookings.filter(
      (b) =>
        b.client_name.toLowerCase().includes(q) ||
        b.service.toLowerCase().includes(q)
    );
  }, [bookings, searchQuery]);

  // Summary stats
  const completed = bookings.filter((b) => b.status === "completed");
  const scheduled = bookings.filter((b) => b.status === "scheduled");
  const revenue = completed.reduce((s, b) => s + b.price, 0);

  function goDay(delta: number) {
    setSelectedDate((d) => d.add(delta, "day"));
  }

  function handleCheckout(booking: SaleBooking) {
    dispatch(resetFlow());
    dispatch(fetchTaxRate());
    dispatch(
      setCheckoutContext({
        bookingId: booking.booking_id,
        clientId: booking.client_id || null,
        clientName: booking.client_name,
        service: booking.service,
        staffId: booking.staff_id,
        amountDue: booking.price,
      })
    );
    navigate("/pos/payment-method");
  }

  async function handleStatusUpdate(bookingId: string, status: SaleBooking["status"]) {
    dispatch(updateBookingStatus({ bookingId, status }));
  }

  function openReschedule(booking: SaleBooking) {
    setReschedulingBooking(booking);
    const start = dayjs(booking.start_time);
    rescheduleForm.setFieldsValue({ date: start, time: start });
  }

  async function handleRescheduleSubmit(values: { date: Dayjs; time: Dayjs }) {
    if (!reschedulingBooking || !businessId) return;
    const durationMins = dayjs(reschedulingBooking.end_time).diff(
      dayjs(reschedulingBooking.start_time),
      "minute"
    );
    const newStart = values.date.hour(values.time.hour()).minute(values.time.minute()).second(0);
    const newEnd = newStart.add(durationMins, "minute");

    setRescheduleSaving(true);
    const result = await dispatch(
      rescheduleBooking({
        bookingId: reschedulingBooking.booking_id,
        startTime: newStart.toISOString(),
        endTime: newEnd.toISOString(),
      })
    );
    setRescheduleSaving(false);

    if (rescheduleBooking.fulfilled.match(result)) {
      messageApi.success("Appointment rescheduled");
      setReschedulingBooking(null);
      dispatch(fetchSalesBookings({ businessId, date: selectedDate.format("YYYY-MM-DD") }));
    } else {
      messageApi.error("Failed to reschedule appointment");
    }
  }

  async function handleViewReceipt(bookingId: string) {
    setReceiptLoading(true);
    try {
      const res = await axiosClient.get("/transactions", {
        params: { booking_id: bookingId },
        headers: authHeader(),
      });
      setViewingReceipt(res.data);
    } catch (err: any) {
      messageApi.error(err.response?.data || "No receipt found for this appointment.");
    } finally {
      setReceiptLoading(false);
    }
  }

  return (
    <div className="admin-sales-page">
      {contextHolder}
      {/* ── Controls Bar ── */}
      <div className="sales-controls-bar">
        <div className="sales-controls-left">
          <h1>Sales & Checkout</h1>
          <div className="sales-date-nav">
            <button className="sales-date-nav-btn" onClick={() => goDay(-1)}>
              <ChevronLeft size={18} />
            </button>
            <button className="sales-date-nav-btn" onClick={() => goDay(1)}>
              <ChevronRight size={18} />
            </button>
            <span className="sales-date-label">
              {selectedDate.format("dddd, MMMM D, YYYY")}
            </span>
          </div>
        </div>
        <Button
          type="primary"
          icon={<Plus size={16} />}
          style={{
            background: "#2563eb",
            borderColor: "#2563eb",
            fontWeight: 600,
            height: 40,
            paddingInline: 20,
          }}
          onClick={openApptModal}
        >
          New Appointment
        </Button>
      </div>

      {/* ── Content ── */}
      <div className="sales-content">
        <div className="sales-grid">

          {/* ── Left Panel ── */}
          <div className="sales-left-panel">
            {/* Mini Calendar */}
            <div className="sales-panel-card">
              <Calendar
                fullscreen={false}
                value={selectedDate}
                onSelect={(date) => setSelectedDate(date)}
              />
              <div className="sales-calendar-actions">
                <Button
                  block
                  type="primary"
                  style={{ background: "#2563eb", borderColor: "#2563eb", fontWeight: 600 }}
                  onClick={() => setSelectedDate(dayjs())}
                >
                  Today
                </Button>
                <Button
                  block
                  onClick={() => setSelectedDate(dayjs().add(1, "day"))}
                >
                  Tomorrow
                </Button>
              </div>
            </div>

            {/* Day Summary */}
            <div className="sales-panel-card">
              <h2>Day Summary</h2>
              <div className="sales-summary-rows">
                <div className="sales-summary-row">
                  <span className="sales-summary-row-label">Total Appointments</span>
                  <span className="sales-summary-row-value">{bookings.length}</span>
                </div>
                <div className="sales-summary-row">
                  <span className="sales-summary-row-label">Completed</span>
                  <span className="sales-summary-row-value green">{completed.length}</span>
                </div>
                <div className="sales-summary-row">
                  <span className="sales-summary-row-label">Upcoming</span>
                  <span className="sales-summary-row-value blue">{scheduled.length}</span>
                </div>
                <hr className="sales-summary-divider" />
                <div className="sales-summary-row">
                  <span className="sales-summary-row-label">
                    <DollarSign size={14} style={{ display: "inline", marginRight: 3 }} />
                    Revenue
                  </span>
                  <span className="sales-summary-row-value">
                    ${revenue.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right Panel ── */}
          <div className="sales-right-panel">
            {/* Search */}
            <div className="sales-search-card">
              <Input.Search
                placeholder="Search by client name or service..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
              />
            </div>

            {/* Appointment Cards */}
            <Spin spinning={loading}>
              {filtered.length === 0 && !loading ? (
                <div className="sales-empty">
                  No appointments for{" "}
                  {selectedDate.format("MMMM D, YYYY")}
                </div>
              ) : (
                filtered.map((booking) => {
                  const tag = STATUS_TAG[booking.status] ?? STATUS_TAG.scheduled;
                  const isUpdating = updatingId === booking.booking_id;

                  return (
                    <div key={booking.booking_id} className="sales-appointment-card">
                      <div className="sales-appt-inner">
                        <div className="sales-appt-left">
                          <div className="sales-appt-avatar">
                            {initials(booking.client_name)}
                          </div>
                          <div>
                            <div className="sales-appt-name">{booking.client_name}</div>
                            <div className="sales-appt-service">{booking.service}</div>
                            <div className="sales-appt-meta">
                              <span className="sales-appt-meta-item">
                                <Clock size={14} />
                                {formatTime(booking.start_time)}
                              </span>
                              <span className="sales-appt-meta-item">
                                {durationMinutes(booking.start_time, booking.end_time)}
                              </span>
                              <span className="sales-appt-price">
                                ${booking.price.toFixed(2)}
                              </span>
                              <Tag color={tag.color}>{tag.label}</Tag>
                            </div>
                          </div>
                        </div>

                        <div className="sales-appt-actions">
                          {booking.status === "scheduled" && (
                            <>
                              <Button
                                type="primary"
                                style={{
                                  background: "#2563eb",
                                  borderColor: "#2563eb",
                                  fontWeight: 600,
                                }}
                                loading={isUpdating}
                                onClick={() => handleCheckout(booking)}
                              >
                                Check Out
                              </Button>
                              <Button onClick={() => openReschedule(booking)}>
                                Reschedule
                              </Button>
                              <Button
                                loading={isUpdating}
                                onClick={() =>
                                  handleStatusUpdate(booking.booking_id, "no_show")
                                }
                              >
                                No Show
                              </Button>
                            </>
                          )}
                          {booking.status === "in_progress" && (
                            <Button
                              style={{
                                background: "#16a34a",
                                borderColor: "#16a34a",
                                color: "#fff",
                                fontWeight: 600,
                              }}
                              loading={isUpdating}
                              onClick={() =>
                                handleStatusUpdate(booking.booking_id, "completed")
                              }
                            >
                              Complete
                            </Button>
                          )}
                          {booking.status === "completed" && (
                            <Button
                              loading={receiptLoading}
                              onClick={() => handleViewReceipt(booking.booking_id)}
                            >
                              View Receipt
                            </Button>
                          )}
                          {(booking.status === "cancelled" ||
                            booking.status === "no_show") && (
                            <Button
                              loading={isUpdating}
                              onClick={() =>
                                handleStatusUpdate(booking.booking_id, "scheduled")
                              }
                            >
                              Restore
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </Spin>
          </div>
        </div>
      </div>
      {/* ── New Appointment Modal ── */}
      <Modal
        open={apptModalOpen}
        onCancel={() => setApptModalOpen(false)}
        title={<span style={{ fontWeight: 700, fontSize: 18 }}>New Appointment</span>}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form
          form={apptForm}
          layout="vertical"
          onFinish={handleApptSubmit}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="client_id"
            label="Client"
            rules={[{ required: true, message: "Select a client" }]}
          >
            <Select
              showSearch
              placeholder="Search client by name..."
              filterOption={(input, opt) =>
                (opt?.label as string ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={clients.map((c) => ({ value: c.client_id, label: c.name }))}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="staff_id"
            label="Staff Member"
            rules={[{ required: true, message: "Select a staff member" }]}
          >
            <Select
              placeholder="Select staff member..."
              options={barberList.map((b) => ({ value: b.id, label: b.name }))}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="service"
            label="Service"
            rules={[{ required: true, message: "Select a service" }]}
          >
            <Select
              placeholder="Select a service..."
              size="large"
              options={services.map((s) => ({
                value: s.name,
                label: `${s.name} — ${s.duration_min} min — $${s.price}`,
              }))}
              onChange={(val) => {
                const svc = services.find((s) => s.name === val);
                if (svc) apptForm.setFieldValue("price", svc.price);
              }}
            />
          </Form.Item>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Form.Item
              name="date"
              label="Date"
              rules={[{ required: true, message: "Pick a date" }]}
            >
              <DatePicker style={{ width: "100%" }} size="large" />
            </Form.Item>

            <Form.Item
              name="start_time"
              label="Start Time"
              rules={[{ required: true, message: "Pick a start time" }]}
            >
              <TimePicker
                style={{ width: "100%" }}
                size="large"
                format="h:mm A"
                minuteStep={15}
                use12Hours
              />
            </Form.Item>
          </div>

          <Form.Item name="price" label="Price ($)">
            <InputNumber
              min={0}
              precision={2}
              style={{ width: "100%" }}
              size="large"
              prefix="$"
            />
          </Form.Item>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
            <Button onClick={() => setApptModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={apptSaving}
              style={{ background: "#2563eb", borderColor: "#2563eb", fontWeight: 600, paddingInline: 24 }}
            >
              Create Appointment
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={!!reschedulingBooking}
        title="Reschedule Appointment"
        onCancel={() => setReschedulingBooking(null)}
        footer={null}
        destroyOnClose
      >
        <Form form={rescheduleForm} layout="vertical" onFinish={handleRescheduleSubmit} style={{ marginTop: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Form.Item name="date" label="Date" rules={[{ required: true, message: "Pick a date" }]}>
              <DatePicker style={{ width: "100%" }} size="large" />
            </Form.Item>
            <Form.Item name="time" label="Start Time" rules={[{ required: true, message: "Pick a start time" }]}>
              <TimePicker style={{ width: "100%" }} size="large" format="h:mm A" minuteStep={15} use12Hours />
            </Form.Item>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
            <Button onClick={() => setReschedulingBooking(null)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={rescheduleSaving}>
              Save New Time
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={!!viewingReceipt}
        title="Receipt"
        onCancel={() => setViewingReceipt(null)}
        footer={<Button onClick={() => setViewingReceipt(null)}>Close</Button>}
      >
        {viewingReceipt && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Amount</span>
              <strong>${viewingReceipt.amount.toFixed(2)}</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Payment Method</span>
              <strong style={{ textTransform: "capitalize" }}>
                {viewingReceipt.payment_method.replace("_", " ")}
              </strong>
            </div>
            {viewingReceipt.payment_method === "cash" && (
              <>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Cash Received</span>
                  <span>${(viewingReceipt.tendered_amount ?? 0).toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Change Due</span>
                  <span>${(viewingReceipt.change_due ?? 0).toFixed(2)}</span>
                </div>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Date</span>
              <span>{new Date(viewingReceipt.created_at).toLocaleString()}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Transaction ID</span>
              <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                {viewingReceipt.transaction_id}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
